/**
 * Rough AI translation cost estimates (USD), token-based.
 * Rates come from server /storage/config (translation.pricing).
 */

/** ~4 characters per token for Latin UI strings. */
const CHARS_PER_TOKEN = 4;

/** Prompt + JSON schema overhead per API request chunk. */
const PROMPT_OVERHEAD_TOKENS = 450;

/** Assume output length ≈ input for locale JSON strings. */
const OUTPUT_CHAR_RATIO = 1;

const DEFAULT_ESTIMATE_CHUNK_CHARS = 28000;

/**
 * @param {string} model
 * @returns {boolean}
 */
export function isTranslationModelActive(model) {
  const name = String(model || '').trim().toLowerCase();
  return Boolean(name) && name !== 'off';
}

/**
 * @param {object} estimate
 * @returns {string}
 */
export function getTranslationRateLabel(estimate) {
  if (!estimate || !isTranslationModelActive(estimate.model)) return '';
  const pricing = estimate.pricing;
  if (!pricing) return estimate.modelLabel || estimate.model || '';

  const label = estimate.modelLabel || estimate.model;
  return `${label} · $${pricing.inputUsdPerM.toFixed(2)}/M in · $${pricing.outputUsdPerM.toFixed(2)}/M out`;
}

function charsToTokens(chars) {
  return Math.ceil(Math.max(0, Number(chars) || 0) / CHARS_PER_TOKEN);
}

/**
 * @param {string[]} texts
 * @returns {number}
 */
export function countTranslationCharacters(texts) {
  if (!Array.isArray(texts)) return 0;
  return texts.reduce((sum, text) => sum + String(text ?? '').length, 0);
}

/**
 * @param {number} sourceChars characters in file (one pass)
 * @param {number} targetLocaleCount
 * @param {number} [estimateChunkChars]
 * @returns {{ inputTokens: number, outputTokens: number, requestCount: number }}
 */
function estimateTranslationTokens(sourceChars, targetLocaleCount, estimateChunkChars) {
  const chars = Math.max(0, Number(sourceChars) || 0);
  const targets = Math.max(0, Number(targetLocaleCount) || 0);
  const chunkChars = Math.max(1, Number(estimateChunkChars) || DEFAULT_ESTIMATE_CHUNK_CHARS);

  if (!chars || !targets) {
    return { inputTokens: 0, outputTokens: 0, requestCount: 0 };
  }

  const perLocaleChars = chars;
  const requestCount = Math.max(1, Math.ceil(perLocaleChars / chunkChars)) * targets;
  const inputTokens =
    targets * charsToTokens(perLocaleChars) + requestCount * PROMPT_OVERHEAD_TOKENS;
  const outputTokens = targets * charsToTokens(perLocaleChars * OUTPUT_CHAR_RATIO);

  return { inputTokens, outputTokens, requestCount };
}

/**
 * @param {object} options
 * @param {number} options.characterCount source characters per target pass
 * @param {number} [options.targetLocaleCount=1]
 * @param {string} [options.model='off']
 * @param {string} [options.modelLabel]
 * @param {{ inputUsdPerM: number, outputUsdPerM: number }} [options.pricing]
 * @param {number} [options.estimateChunkChars]
 */
export function estimateTranslationCost({
  characterCount,
  targetLocaleCount = 1,
  model = 'off',
  modelLabel = '',
  pricing = null,
  estimateChunkChars = DEFAULT_ESTIMATE_CHUNK_CHARS
}) {
  const chars = Math.max(0, Number(characterCount) || 0);
  const targets = Math.max(0, Number(targetLocaleCount) || 0);
  const inputCharacters = chars * targets;
  const modelName = String(model || 'off');

  if (!isTranslationModelActive(modelName) || inputCharacters === 0 || !pricing) {
    return {
      characterCount: chars,
      inputCharacters,
      estimatedOutputCharacters: 0,
      estimatedUsd: 0,
      model: modelName,
      modelLabel: modelLabel || modelName,
      pricing: pricing || null
    };
  }

  const { inputTokens, outputTokens } = estimateTranslationTokens(
    chars,
    targets,
    estimateChunkChars
  );
  const estimatedUsd =
    (inputTokens / 1_000_000) * pricing.inputUsdPerM +
    (outputTokens / 1_000_000) * pricing.outputUsdPerM;

  return {
    characterCount: chars,
    inputCharacters,
    estimatedOutputCharacters: Math.round(inputCharacters * OUTPUT_CHAR_RATIO),
    estimatedUsd,
    model: modelName,
    modelLabel: modelLabel || modelName,
    pricing,
    inputTokens,
    outputTokens
  };
}

/**
 * @param {number} usd
 * @returns {string}
 */
export function formatEstimateUsd(usd) {
  const value = Math.max(0, Number(usd) || 0);
  if (value === 0) return '$0.00';
  if (value < 0.01) return '<$0.01';
  if (value < 1) return `~$${value.toFixed(2)}`;
  return `~$${value.toFixed(2)}`;
}

/**
 * @param {number} count
 * @returns {string}
 */
export function formatCharacterCount(count) {
  const n = Math.max(0, Number(count) || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

/**
 * @param {object} estimate
 * @param {number} [targetLocaleCount]
 * @returns {string}
 */
export function formatTranslationCostDetail(estimate, targetLocaleCount = 1) {
  if (!estimate || estimate.inputCharacters === 0) return '';
  if (!isTranslationModelActive(estimate.model)) return '';

  const sourceChars = formatCharacterCount(estimate.characterCount);
  const billedInput = formatCharacterCount(estimate.inputCharacters);
  const billedOut = formatCharacterCount(estimate.estimatedOutputCharacters);
  const targets = Math.max(1, Number(targetLocaleCount) || 1);
  const tokens =
    estimate.inputTokens != null && estimate.outputTokens != null
      ? ` · ~${formatCharacterCount(estimate.inputTokens)}+${formatCharacterCount(estimate.outputTokens)} tok`
      : '';

  if (targets > 1) {
    return `~${sourceChars} chars × ${targets} langs → ~${billedInput} in + ~${billedOut} out${tokens}`;
  }
  return `~${sourceChars} chars → ~${billedInput} in + ~${billedOut} out${tokens}`;
}
